import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useProductMatrix } from '../contexts/ProductMatrixContext';

const COUNTRIES = [
  { code: 'US', flag: '🇺🇸' },
  { code: 'Canada', flag: '🇨🇦' },
  { code: 'UK', flag: '🇬🇧' },
  { code: 'Ireland', flag: '🇮🇪' },
  { code: 'Germany', flag: '🇩🇪' },
  { code: 'France', flag: '🇫🇷' },
  { code: 'Spain', flag: '🇪🇸' },
  { code: 'Italy', flag: '🇮🇹' },
  { code: 'Netherlands', flag: '🇳🇱' },
  { code: 'Belgium', flag: '🇧🇪' },
  { code: 'Austria', flag: '🇦🇹' },
  { code: 'Switzerland', flag: '🇨🇭' },
  { code: 'Sweden', flag: '🇸🇪' },
  { code: 'Norway', flag: '🇳🇴' },
  { code: 'Denmark', flag: '🇩🇰' },
  { code: 'Finland', flag: '🇫🇮' },
  { code: 'Poland', flag: '🇵🇱' },
  { code: 'Czechia', flag: '🇨🇿' },
  { code: 'Hungary', flag: '🇭🇺' },
  { code: 'Lithuania', flag: '🇱🇹' },
  { code: 'Australia', flag: '🇦🇺' },
  { code: 'New Zealand', flag: '🇳🇿' },
  { code: 'Brazil', flag: '🇧🇷' },
  { code: 'Israel', flag: '🇮🇱' },
];

function getCountryFlag(countryName: string): string {
  const c = countryName.toLowerCase();
  if (c.includes('united states') || c.includes('usa') || c === 'us') return '🇺🇸';
  if (c.includes('canada')) return '🇨🇦';
  if (c.includes('united kingdom') || c.includes('uk')) return '🇬🇧';
  if (c.includes('germany')) return '🇩🇪';
  if (c.includes('france')) return '🇫🇷';
  if (c.includes('italy')) return '🇮🇹';
  if (c.includes('spain')) return '🇪🇸';
  if (c.includes('netherlands')) return '🇳🇱';
  if (c.includes('belgium')) return '🇧🇪';
  if (c.includes('poland')) return '🇵🇱';
  if (c.includes('ireland')) return '🇮🇪';
  if (c.includes('austria')) return '🇦🇹';
  if (c.includes('australia')) return '🇦🇺';
  if (c.includes('new zealand')) return '🇳🇿';
  return '🌍';
}

type UseRouteSyncOptions = {
  platformName?: string;
  region?: string;
  country?: string;
  countryCode?: string;
  searchParams: URLSearchParams;
  searchProducts: (filters: Record<string, string>) => void;
};

export function useRouteSync({
  platformName,
  region,
  country,
  countryCode,
  searchParams,
  searchProducts,
}: UseRouteSyncOptions): void {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    setSelectedRegion,
    setSelectedCountry,
    setSelectedPlatform,
    setCurrentView,
    setBrowseMode,
    setSelectedOnboardingCountry,
  } = useProductMatrix();

  useEffect(() => {
    const pathname = location.pathname;

    if (region) setSelectedRegion(decodeURIComponent(region));
    if (country) setSelectedCountry(decodeURIComponent(country));

    const queryRegion = searchParams.get('region');
    const queryCountry = searchParams.get('country');

    if (queryRegion && !region && pathname === '/') {
      navigate(`/location/${encodeURIComponent(queryRegion)}`, { replace: true });
      return;
    }
    if (queryRegion && !region && pathname.startsWith('/location')) {
      navigate(`/location/${encodeURIComponent(queryRegion)}`, { replace: true });
      return;
    }
    if (queryCountry && !country && !region && pathname === '/') {
      navigate(`/browse?country=${queryCountry}`, { replace: true });
      return;
    }

    if (pathname.startsWith('/location')) {
      setCurrentView('location');
    } else if (pathname === '/browse') {
      setCurrentView('selection');
      setBrowseMode(true);
      if (queryCountry) setSelectedCountry(queryCountry);
    } else if (pathname.startsWith('/platform/') && platformName) {
      const decoded = decodeURIComponent(platformName);
      setSelectedPlatform(decoded);
      setCurrentView('platform');
      setBrowseMode(!queryCountry && !queryRegion);
      if (queryCountry) setSelectedCountry(queryCountry);
      if (queryRegion) setSelectedRegion(queryRegion);
    } else if (pathname === '/onboarding') {
      setCurrentView('selection');
      setBrowseMode(true);
      setSelectedOnboardingCountry(null);
    } else if (pathname.startsWith('/onboarding/') && countryCode) {
      const decoded = decodeURIComponent(countryCode);
      const countryNameMap: Record<string, string> = {
        US: 'United States',
        Germany: 'Germany',
      };
      const countryName = countryNameMap[decoded] || decoded;
      const found = COUNTRIES.find(c => c.code === decoded);
      setSelectedOnboardingCountry({
        country: decoded,
        countryName,
        countryFlag: found?.flag || getCountryFlag(decoded),
        systems: [],
      });
      setCurrentView('onboarding');
    } else {
      setCurrentView('selection');
      setBrowseMode(true);
      searchProducts({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    location.pathname,
    searchParams,
    platformName,
    region,
    country,
    countryCode,
    navigate,
    searchProducts,
  ]);
}
